import {settings} from "@/settings";
import {api} from "@/auth/api";
import {
    EmailMessage,
    NoticeContent,
    NoticeMessage,
    NoticeTemplate,
    NoticeTemplateSelectionContext, PackNoticeTemplateInfo,
} from "@/types";
import {useApi} from "@/requests/useApi";
import {headers, UON} from "@/utils";
import {orderUserEmailsMock} from "@/store/services/task/mocks/orderUserEmails.mock";
import {checkRecipientWhitelist} from "@/utils/recipient-whitelist";

const systemUid = `odm-contactmechanism`
const baseUrl = `${settings.api_url}/${systemUid}/api/1/${systemUid}`
const baseUrlGenerated = `${settings.api_url}/${systemUid}/api/1/event/${systemUid}`

export async function getAvailableNoticeTemplateList(request: NoticeTemplateSelectionContext): Promise<NoticeTemplate[]> {
    return (await api().post(`${baseUrl}/noticetemplate/`, request)).data
}

export async function getNoticeTemplatePackItems(request: NoticeTemplateSelectionContext, packNoticeTemplateUid: string): Promise<PackNoticeTemplateInfo[]> {
    return (await api().post(`${baseUrl}/noticetemplate/${packNoticeTemplateUid}/packItems/`, request)).data
}

export async function getDocumentGenerated(request: NoticeMessage): Promise<any> {
    return (await api().post(`${baseUrlGenerated}/notice/view/`, request)).data
}
export async function getDocumentDownload(request: NoticeContent): Promise<any> {
    return (await api().post(`${baseUrlGenerated}/notice/download/`, request)).data
}
// Tous les envois du front passent ici : la whitelist des destinataires (environnements de test) est contrôlée avant l'appel.
export async function sendMail(request: EmailMessage): Promise<any> {
    if (!(await checkRecipientWhitelist(request, 'email'))) return undefined
    return (await api().post(`${baseUrlGenerated}/email/`, request)).data
}
export async function sendMailICS(request: EmailMessage): Promise<any> {
    if (!(await checkRecipientWhitelist(request, 'email'))) return undefined
    return (await api().post(`${baseUrlGenerated}/emailICS/`, request)).data
}

export async function sendSms(request: any): Promise<any> {
    if (!(await checkRecipientWhitelist(request, 'sms'))) return undefined
    return (await api().post(`${baseUrlGenerated}/sms/`, request)).data
}

export async function getNoticeTemplateBody(templateResourceUid?: string): Promise<any> {
    const modelBaseUrl = `${settings.api_url}/${systemUid}/api/1/model/${systemUid}`
    return (await api().get(`${modelBaseUrl}/emailtemplate/${templateResourceUid || 'SendNoticeTemplate'}/`)).data
}

/**
 * get historic messages based on the offer reference
 * @param payload
 */
export const getHistoricMessagesApi = async (payload: { type: string, entityId: string }) => {
    return await api().get(settings.api_query_url, {
        headers: headers({
            qc: JSON.stringify({
                "queryId": "contactmechanism-offer-messages",
                "offset": 0,
                "limit": 300,
                "parameters": { "reference": payload?.entityId }
            })
        })
    });
}

/**
 * Récupère les events de communication esign (client_application_objecttype = 'odm.esign.esignclient')
 * rattachés à une référence de contrat et à l'email du signataire, via la query dataservice
 * esign-client-communication-events. Les deux paramètres sont obligatoires côté query
 * (filtres exacts sur _business_data.contract_reference et _business_data.signatory_email).
 * @param reference référence de contrat (_business_data.contract_reference)
 * @param email email du signataire (_business_data.signatory_email)
 */
export const getEsignClientEventsApi = async (reference: string, email: string) => {
    return await api().get(settings.api_query_url, {
        headers: headers({
            qc: JSON.stringify({
                "queryId": "esign-client-communication-events",
                "offset": 0,
                "limit": 300,
                "parameters": { "reference": reference, "email": email }
            })
        })
    });
}

/**
 * Récupère les emails (EmailEvent) dont l'utilisateur passé en paramètre est expéditeur ou
 * destinataire (to / cc / cci), via la query dataservice order-user-emails. La query ne scope
 * pas par commande (contrainte du parser sur les clés JSONB en casse mixte) : le rattachement
 * à une référence de commande est fait côté appelant à partir de _business_data (businessdata).
 * @param email email de l'utilisateur connecté ; encadré de % ici car les colonnes destinataires
 *              sont multi-valuées (séparateur ";").
 */
const USE_ORDER_USER_EMAILS_MOCK = false;

export const getOrderUserEmailsApi = async (email: string) => {
    if (USE_ORDER_USER_EMAILS_MOCK) {
        // Réponse façonnée comme axios ({ data }) : le store lit res?.data.
        return { data: orderUserEmailsMock } as any;
    }
    return await api().get(settings.api_query_url, {
        headers: headers({
            qc: JSON.stringify({
                "queryId": "order-user-emails",
                "offset": 0,
                "limit": -1,
                "parameters": { "email": `%${email}%` }
            })
        })
    });
}
